import { type FC, useCallback } from 'react';

import { message } from '@vakantiesnl/components/src/utils';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useGetDaysText, useGetNightsText } from '@vakantiesnl/services/src/hooks/utils';
import { getFormattedRoundedPrice } from '@vakantiesnl/services/src/util/mathUtils';
import { type MicroCopy } from '@vakantiesnl/types';
import dayjs from 'dayjs';

import { useCustomStyles } from './PriceBar.styles';
import { Radio } from '../../atoms/Radio';

export type PriceBarProps = {
	/** Microcopies */
	microCopies: MicroCopy;
	/** If the pricebar is selected */
	selected?: boolean;
	/** If the pricebar is the lowest */
	lowest?: boolean;
	/** Rating between 0 and 1 to determine the height */
	rating: number;
	/** Click event to dispatch on click */
	onPriceBarClick: (id: string) => void;
	/** Unique offer identifier */
	id: string;
	/** Number of nights */
	durationNights: number;
	/** Number of nights */
	durationDays: number;
	/** Date of the price bar */
	departureDate: string;
	/** Price of the price bar */
	price: number;
};

export const PriceBar: FC<PriceBarProps> = ({
	microCopies,
	selected,
	lowest,
	onPriceBarClick,
	rating,
	durationDays,
	durationNights,
	departureDate,
	price,
	id,
}) => {
	const { classes, cx } = useCustomStyles({ rating });
	const { isMobile } = useBreakpoints();
	const daysLabel = useGetDaysText(durationDays, isMobile, microCopies);
	const nightsLabel = useGetNightsText(durationNights, microCopies);

	const handleOnSelect = useCallback(() => {
		onPriceBarClick(id);
	}, [onPriceBarClick, id]);

	const dayjsDepartureDate = dayjs(departureDate, 'YYYY-MM-DD');

	return (
		<li className={classes.columnContainer}>
			<label
				className={cx(classes.priceBarContainer, lowest && classes.lowest, selected && classes.selected)}
				data-cy={`${selected ? 'priceBar-selected' : 'priceBar'}`}
			>
				<div className={classes.priceBar}>
					<div className={classes.priceContainer}>
						<div className={classes.priceLabel}>
							<span className={classes.textFrom}>
								{message(microCopies['common.dynamicFrom'], { isMobile })}
							</span>
							<span className={classes.textPrice} data-cy="priceBar-textPrice">
								{getFormattedRoundedPrice(price)}
							</span>
						</div>
					</div>
					<div>
						<div className={classes.tripDuration}>
							<span className={classes.textDays}>{daysLabel}</span>
							{!isMobile && <span className={classes.textNight}>({nightsLabel})</span>}
						</div>
					</div>
				</div>
				<div className={classes.priceBarSelect}>
					<p className={classes.priceBarDate} data-cy="priceBar-date">
						{isMobile ? (
							<>
								<span>{dayjsDepartureDate.format('dd')}</span>
								<br />
								<span>{dayjsDepartureDate.format('D MMM')}</span>
							</>
						) : (
							<span>{dayjsDepartureDate.format('dd D MMM')}</span>
						)}
					</p>
					<Radio checked={!!selected} onChange={handleOnSelect} />
				</div>
			</label>
		</li>
	);
};
